import React, { useEffect, useReducer } from "react";

import { faCalendar, faSignalStream } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";

import { useRecordIconShouldBeVisible } from "~/meetingRecorder/RecordIcon";
import MenuLink, { type MenuLinkProps } from "~/nav/panels/Home/Links/MenuLink";
import { formatDate } from "~/nav/panels/Home/utils";
import { ConnectedIcon } from "~/stream/ConnectedIcon";

import GroupsMenu from "./GroupsMenu";

import type { StreamLink_stream$data as Stream } from "~/__generated__/StreamLink_stream.graphql";

import styles from "~/nav/panels/shared/PanelSection.module.css";

const calendarIcon = <FontAwesomeIcon icon={faCalendar} fixedWidth />;

export default function MeetingStreamLink(
	props: Omit<MenuLinkProps, "to" | "innerRef" | "activeClassName" | "streamId"> & {
		stream: Stream;
		innerRef?: React.Ref<HTMLAnchorElement>;
	},
) {
	const [isMenuOpen, setIsMenuOpen] = React.useState(false);

	const { stream, innerRef, ...restProps } = props;
	const { displayName, linkedEvent, latestNote } = stream;
	const date = formatDate(linkedEvent ? linkedEvent.nextOccurrence?.start : latestNote?.eventStart);
	let eventGuid = latestNote?.event?.guid;
	eventGuid = eventGuid?.includes("_") ? eventGuid.split("_")[0] : eventGuid;

	// Hook to dynamically update meeting "in progress" status
	const [updated, forceUpdate] = useReducer(x => x + 1, 0);
	useEffect(() => {
		if (
			!latestNote ||
			!latestNote.eventStart ||
			!latestNote.eventEnd ||
			latestNote.finished ||
			moment().isAfter(latestNote.eventEnd)
		) {
			return;
		}

		// Re-render component to check progress status every 60 seconds
		// Repeated checks needed to prevent wrong timeout if computer sleeps (https://stackoverflow.com/a/6347336/5594265)
		const inProgressInterval = setInterval(forceUpdate, 60000);
		return () => clearInterval(inProgressInterval);
	}, [latestNote, updated]);

	let inProgress;
	if (latestNote) {
		const { eventStart, eventEnd, finished } = latestNote;
		inProgress = eventStart && eventEnd && moment().isBetween(eventStart, eventEnd) && !finished;
	}
	const title = displayName !== "" ? displayName : "Untitled note series";
	const recordIconShouldBeVisible = useRecordIconShouldBeVisible(latestNote?.event ?? null);

	// We don't use `url` because it will send to the meetings page
	// we want to keep the user in the streams page
	return (
		<div className={styles.wrapper}>
			<MenuLink
				{...restProps}
				innerRef={innerRef}
				to={`/stream/${stream.id}/`}
				className={cx({ [styles.untitledStream]: displayName === "" })}
				activeClassName={styles.active}
				streamId={props.stream.id}
				eventGuid={eventGuid}
				showRecordIcon={recordIconShouldBeVisible}
				icon={calendarIcon}
				iconTooltip={recordIconShouldBeVisible ? "This meeting will be recorded" : "Meeting"}
			>
				<span title={title} className={styles.title}>
					{(stream.isExternal || stream.hasExternalUsers) && <ConnectedIcon className={styles.connectedIcon} />}
					{title}
				</span>
				{!isMenuOpen && date && (
					<span className={cx(styles.date, { [styles.inProgress]: inProgress })}>
						{inProgress && <FontAwesomeIcon icon={faSignalStream} />}
						{date}
					</span>
				)}
			</MenuLink>
			<GroupsMenu stream={stream} isMenuOpen={isMenuOpen} toggleOpen={setIsMenuOpen} />
		</div>
	);
}
